import { normalizeClaimedByForDisplay } from "@/features/homeowners/lib/claimed-by-display"

export const PRIORITY_FILTER_KEYS = {
  addedFrom: "pmAddedFrom",
  addedTo: "pmAddedTo",
  claimedBy: "pmClaimedBy",
  workscopeStatus: "pmWorkscopeStatus",
} as const

export type PriorityCardFilterFields = {
  createdAt?: string | null
  claimedBy?: string | null
  statusDetail?: string | null
}

export type PriorityMatrixFilters = {
  addedFrom: string
  addedTo: string
  claimedBy: string[]
  workscopeStatus: string[]
}

export function parseCommaList(value: string | null): string[] {
  if (!value) return []
  return value.split(",").map((item) => item.trim()).filter(Boolean)
}

export function getPriorityFiltersFromSearchParams(searchParams: {
  get: (key: string) => string | null
}): PriorityMatrixFilters {
  return {
    addedFrom: searchParams.get(PRIORITY_FILTER_KEYS.addedFrom) || "",
    addedTo: searchParams.get(PRIORITY_FILTER_KEYS.addedTo) || "",
    claimedBy: parseCommaList(searchParams.get(PRIORITY_FILTER_KEYS.claimedBy)),
    workscopeStatus: parseCommaList(searchParams.get(PRIORITY_FILTER_KEYS.workscopeStatus)),
  }
}

export function hasActivePriorityFilters(filters: PriorityMatrixFilters): boolean {
  return Boolean(
    filters.addedFrom ||
    filters.addedTo ||
    filters.claimedBy.length ||
    filters.workscopeStatus.length
  )
}

export function applyPriorityFilterSearchParams(
  current: URLSearchParams,
  updates: {
    addedFrom?: string
    addedTo?: string
    claimedBy?: string
    workscopeStatus?: string
  }
): URLSearchParams {
  const next = new URLSearchParams(current.toString())
  next.set("tab", "priority")
  const setOrDelete = (key: string, value?: string) => {
    if (value) next.set(key, value)
    else next.delete(key)
  }
  setOrDelete(PRIORITY_FILTER_KEYS.addedFrom, updates.addedFrom)
  setOrDelete(PRIORITY_FILTER_KEYS.addedTo, updates.addedTo)
  setOrDelete(PRIORITY_FILTER_KEYS.claimedBy, updates.claimedBy)
  setOrDelete(PRIORITY_FILTER_KEYS.workscopeStatus, updates.workscopeStatus)
  return next
}

export function clearPriorityFilterParams(current: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(current.toString())
  next.set("tab", "priority")
  Object.values(PRIORITY_FILTER_KEYS).forEach((key) => next.delete(key))
  return next
}

function claimedByValuesFromCard(claimedBy?: string | null): string[] {
  if (!claimedBy) return [normalizeClaimedByForDisplay("Unclaimed")]
  const trimmed = claimedBy.trim()
  if (!trimmed) return [normalizeClaimedByForDisplay("Unclaimed")]
  if (trimmed.startsWith("[")) {
    try {
      const parsed = JSON.parse(trimmed)
      if (Array.isArray(parsed)) {
        const values = parsed.map(normalizeClaimedByForDisplay).filter(Boolean)
        return values.length > 0 ? values : [normalizeClaimedByForDisplay("Unclaimed")]
      }
    } catch {
      // Fall through to comma-separated parsing
    }
  }
  const values = trimmed.split(",").map((value) => normalizeClaimedByForDisplay(value.trim())).filter(Boolean)
  return values.length > 0 ? values : [normalizeClaimedByForDisplay("Unclaimed")]
}

function cardStatusDetail(statusDetail?: string | null): string {
  if (statusDetail == null || statusDetail === "") return "Unknown"
  return statusDetail
}

export function priorityCardMatchesFilters(
  item: PriorityCardFilterFields,
  filters: PriorityMatrixFilters,
): boolean {
  if (filters.addedFrom || filters.addedTo) {
    const created = item.createdAt ? new Date(item.createdAt).getTime() : null
    if (created == null || Number.isNaN(created)) return false
    if (filters.addedFrom && /^\d{4}-\d{2}-\d{2}$/.test(filters.addedFrom)) {
      const fromStart = new Date(`${filters.addedFrom}T00:00:00`).getTime()
      if (created < fromStart) return false
    }
    if (filters.addedTo && /^\d{4}-\d{2}-\d{2}$/.test(filters.addedTo)) {
      const toEnd = new Date(`${filters.addedTo}T23:59:59.999`).getTime()
      if (created > toEnd) return false
    }
  }

  if (filters.claimedBy.length > 0) {
    const selected = filters.claimedBy.map(normalizeClaimedByForDisplay).filter(Boolean)
    const cardValues = claimedByValuesFromCard(item.claimedBy)
    if (!cardValues.some((value) => selected.includes(value))) return false
  }

  if (filters.workscopeStatus.length > 0) {
    if (!filters.workscopeStatus.includes(cardStatusDetail(item.statusDetail))) return false
  }

  return true
}
